import * as pes from './crypto/pes1.js';
import { Api } from './lib/api.js';
import { Vault, childrenOf, joinPath, parentPath, baseName } from './lib/vault.js';
import { encodeRecovery, decodeRecovery } from './lib/recovery.js';
import * as unlock from './lib/unlockStore.js';

const $ = (sel) => document.querySelector(sel);
const MAX_WEB_FILE = 200 * 1024 * 1024;
const MAX_TEXT_BYTES = 5 * 1024 * 1024;
const MAX_PREVIEW_TEXT = 200_000;

const authMsg = $('#auth-msg');
const statusEl = $('#status');

const state = {
  mode: 'login',
  api: null,
  vault: null,
  userId: null,
  keys: null,
  salt: null,
  kdf: pes.DEFAULT_KDF,
  masterKey: null,
  entries: [],
  cwd: '/',
  busy: false,
  previewText: '',
};

const setMsg = (el, text, isError = false) => {
  el.textContent = text;
  el.classList.toggle('error', Boolean(isError));
};
const tick = () => new Promise((r) => setTimeout(r, 0));

const fmtSize = (n) => {
  if (n === undefined || n === null) return '';
  const units = ['B', 'KB', 'MB', 'GB'];
  let i = 0;
  let v = n;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i++;
  }
  return `${v.toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
};
const fmtTime = (ms) => (ms ? new Date(ms).toLocaleString('zh-CN', { hour12: false }) : '');

function upsert(entries, entry) {
  const next = entries.filter((e) => e.path !== entry.path);
  next.push(entry);
  return next;
}

function showRecovery(salt, masterKey) {
  $('#recovery-text').textContent = encodeRecovery(salt, masterKey);
  $('#recovery-dialog').showModal();
}

function enterMain({ api, userId, keys, salt, kdf, masterKey, username, remember = false }) {
  Object.assign(state, { api, userId, keys, salt, kdf, masterKey });
  state.vault = new Vault({ api, userId, keys, salt, kdf });
  state.cwd = '/';
  $('#auth').hidden = true;
  $('#main').hidden = false;
  localStorage.setItem('pes.server', api.base);
  localStorage.setItem('pes.username', username);
  const record = { username, server: api.base, masterKey: pes.toHex(masterKey) };
  unlock.saveSession(record);
  if (remember) unlock.savePersistent(record).then((ok) => {
    if (!ok) setMsg(statusEl, '浏览器不允许持久保存密钥，本次仅记住到关闭标签页。');
  });
  return refresh();
}

async function refresh() {
  setMsg(statusEl, '正在拉取索引…');
  const doc = await state.vault.loadManifest();
  state.entries = doc.entries;
  render();
  setMsg(statusEl, `已同步：revision ${doc.revision}，共 ${doc.entries.length} 项`);
}

function render() {
  const crumbs = $('#crumbs');
  crumbs.textContent = '';
  const parts = state.cwd.split('/').filter(Boolean);
  const home = document.createElement('a');
  home.textContent = '全部文件';
  home.onclick = () => {
    state.cwd = '/';
    render();
  };
  crumbs.append(home);
  let acc = '';
  for (const part of parts) {
    acc += '/' + part;
    const target = acc;
    crumbs.append(document.createTextNode(' / '));
    const a = document.createElement('a');
    a.textContent = part;
    a.onclick = () => {
      state.cwd = target;
      render();
    };
    crumbs.append(a);
  }

  const rows = $('#rows');
  rows.textContent = '';
  const items = childrenOf(state.entries, state.cwd);
  if (items.length === 0) {
    const tr = document.createElement('tr');
    const td = document.createElement('td');
    td.colSpan = 4;
    td.className = 'empty';
    td.textContent = '这里还是空的，点「上传」传文件，或「新建文本」记一段话。';
    tr.append(td);
    rows.append(tr);
    return;
  }

  for (const item of items) {
    const tr = document.createElement('tr');

    const nameTd = document.createElement('td');
    const nameEl = document.createElement('span');
    nameEl.className = 'name' + (item.kind === 'dir' ? ' dir' : '');
    nameEl.textContent = (item.kind === 'dir' ? '📁 ' : '📄 ') + baseName(item.path) + (item.synthetic ? '（隐式目录）' : '');
    nameTd.append(nameEl);
    if (item.kind === 'dir') {
      nameEl.onclick = () => {
        state.cwd = item.path;
        render();
      };
    } else if (isPreviewable(item.path)) {
      nameEl.classList.add('clickable');
      nameEl.onclick = () => preview(item);
      nameEl.title = '点击预览';
    }

    const sizeTd = document.createElement('td');
    sizeTd.className = 'num';
    sizeTd.textContent = item.kind === 'file' ? fmtSize(item.size) : '';

    const timeTd = document.createElement('td');
    timeTd.className = 'num';
    timeTd.textContent = fmtTime(item.mtime);

    const opsTd = document.createElement('td');
    opsTd.className = 'ops';
    if (item.kind === 'file' && !item.synthetic) {
      if (isPreviewable(item.path)) {
        const pv = document.createElement('button');
        pv.textContent = '预览';
        pv.onclick = () => preview(item);
        opsTd.append(pv);
      }
      const dl = document.createElement('button');
      dl.textContent = '下载';
      dl.onclick = () => download(item);
      opsTd.append(dl);
    }
    if (!item.synthetic) {
      const del = document.createElement('button');
      del.textContent = '删除';
      del.onclick = () => remove(item);
      opsTd.append(del);
    }

    tr.append(nameTd, sizeTd, timeTd, opsTd);
    rows.append(tr);
  }
}

const IMAGE_RE = /\.(png|jpe?g|gif|webp|bmp|avif)$/i;
const TEXT_RE = /\.(txt|text|md|markdown|json|log|csv|tsv|ya?ml|xml|ini|conf|cfg|srt|js|mjs|ts|css|html?|py|kt|java|go|rs|c|h|cpp|sh|ps1|sql)$/i;
const isImage = (path) => IMAGE_RE.test(path);
const isText = (path) => TEXT_RE.test(path);
const isPreviewable = (path) => isImage(path) || isText(path);

async function preview(entry) {
  if (state.busy) return;
  state.busy = true;
  let url;
  const img = $('#preview-img');
  const pre = $('#preview-text');
  const note = $('#preview-note');
  const copyBtn = $('#preview-copy');
  try {
    setMsg(statusEl, `正在解密 ${baseName(entry.path)} …`);
    const blob = await state.vault.downloadBlob(entry);
    $('#preview-title').textContent = baseName(entry.path);
    if (isImage(entry.path)) {
      url = URL.createObjectURL(blob);
      img.src = url;
      img.hidden = false;
      pre.hidden = true;
      pre.textContent = '';
      note.hidden = true;
      copyBtn.hidden = true;
      state.previewText = '';
    } else {
      const text = await blob.text();
      state.previewText = text;
      const truncated = text.length > MAX_PREVIEW_TEXT;
      img.hidden = true;
      img.removeAttribute('src');
      pre.textContent = truncated ? text.slice(0, MAX_PREVIEW_TEXT) : text;
      pre.hidden = false;
      note.textContent = truncated
        ? `仅显示前 ${MAX_PREVIEW_TEXT.toLocaleString()} 个字符（共 ${text.length.toLocaleString()} 个），完整内容请下载。`
        : '';
      note.hidden = !truncated;
      copyBtn.hidden = false;
    }
    const dialog = $('#preview-dialog');
    dialog.onclose = () => {
      if (url) URL.revokeObjectURL(url);
      img.removeAttribute('src');
      pre.textContent = '';
      state.previewText = '';
    };
    dialog.showModal();
    setMsg(statusEl, '');
  } catch (e) {
    if (url) URL.revokeObjectURL(url);
    setMsg(statusEl, e.message, true);
  } finally {
    state.busy = false;
  }
}

async function download(entry) {
  if (state.busy) return;
  state.busy = true;
  try {
    setMsg(statusEl, `正在下载并解密 ${baseName(entry.path)} …`);
    const blob = await state.vault.downloadBlob(entry);
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = baseName(entry.path);
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
    setMsg(statusEl, `已解密 ${baseName(entry.path)}（${fmtSize(blob.size)}）`);
  } catch (e) {
    setMsg(statusEl, e.message, true);
  } finally {
    state.busy = false;
  }
}

async function remove(item) {
  if (state.busy) return;
  const label = item.kind === 'dir' ? '目录（其中文件不会被删除）' : '文件';
  if (!(await askConfirm(`确定删除${label} ${baseName(item.path)} ？`))) return;
  state.busy = true;
  try {
    if (item.kind === 'file') await state.vault.deleteObject(item);
    await state.vault.commit((entries) => entries.filter((e) => e.path !== item.path));
    await refresh();
  } catch (e) {
    setMsg(statusEl, e.message, true);
  } finally {
    state.busy = false;
  }
}

async function uploadFiles(files) {
  if (state.busy || files.length === 0) return;
  state.busy = true;
  try {
    for (const file of files) {
      if (file.size > MAX_WEB_FILE) {
        setMsg(statusEl, `跳过 ${file.name}：网页端单文件上限 200 MB，请用 Android 端上传。`, true);
        continue;
      }
      const path = joinPath(state.cwd, file.name);
      setMsg(statusEl, `正在加密 ${file.name} …`);
      await tick();
      const entry = await state.vault.uploadFile(path, file, (loaded, total, phase) => {
        if (phase === 'encrypt') {
          setMsg(statusEl, `正在加密 ${file.name}…`);
        } else {
          setMsg(statusEl, `上传 ${file.name}：${Math.round((loaded / total) * 100)}%`);
        }
      });
      await state.vault.commit((entries) => upsert(entries, entry));
      setMsg(statusEl, `已上传 ${file.name}（${fmtSize(file.size)}）`);
    }
    await refresh();
  } catch (e) {
    setMsg(statusEl, e.message, true);
  } finally {
    state.busy = false;
    $('#file-input').value = '';
  }
}

async function uploadText(name, text) {
  if (state.busy || !name) return;
  state.busy = true;
  try {
    const path = uniquePath(state.cwd, name);
    const blob = new Blob([text], { type: 'text/plain;charset=utf-8' });
    setMsg(statusEl, `正在加密 ${baseName(path)} …`);
    await tick();
    const entry = await state.vault.uploadFile(path, blob);
    await state.vault.commit((entries) => upsert(entries, entry));
    draftDirty = false;
    await refresh();
    setMsg(statusEl, `已保存 ${baseName(path)}（${fmtSize(blob.size)}）`);
  } catch (e) {
    setMsg(statusEl, e.message, true);
  } finally {
    state.busy = false;
  }
}

const pad2 = (n) => String(n).padStart(2, '0');
function stampName(d = new Date()) {
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())} ${pad2(d.getHours())}-${pad2(d.getMinutes())}`;
}

function suggestTextName(text) {
  const firstLine = (text.split(/\r?\n/).find((line) => line.trim()) ?? '').trim();
  const cleaned = firstLine.slice(0, 20).replace(/[\\/:*?"<>|\u0000-\u001f]/g, '').trim();
  return `${cleaned || `笔记 ${stampName()}`}.txt`;
}

function uniquePath(dir, name) {
  const exists = (p) => state.entries.some((e) => e.path === p);
  if (!exists(joinPath(dir, name))) return joinPath(dir, name);
  const dot = name.lastIndexOf('.');
  const stem = dot > 0 ? name.slice(0, dot) : name;
  const ext = dot > 0 ? name.slice(dot) : '';
  for (let i = 2; i < 1000; i++) {
    const candidate = joinPath(dir, `${stem} (${i})${ext}`);
    if (!exists(candidate)) return candidate;
  }
  return joinPath(dir, `${stem} (${Date.now()})${ext}`);
}

let draftDirty = false;
let autoName = '';

function updateTextMeta() {
  const body = $('#text-body').value;
  const bytes = new TextEncoder().encode(body).length;
  $('#text-hint').textContent = `${body.length} 字符 / ${fmtSize(bytes)}，保存前在浏览器内加密。`;
  $('#text-hint').classList.toggle('error', bytes > MAX_TEXT_BYTES);
  $('#text-save').disabled = body.length === 0 || bytes > MAX_TEXT_BYTES;
}

function openTextDialog(prefill = '') {
  if (!state.vault || state.busy) return;
  const bytes = new TextEncoder().encode(prefill).length;
  if (bytes > MAX_TEXT_BYTES) {
    setMsg(statusEl, `文本有 ${fmtSize(bytes)}，超过 5 MB 上限，请改用「上传」保存成文件。`, true);
    return;
  }
  $('#text-name').value = suggestTextName(prefill);
  autoName = $('#text-name').value;
  $('#text-body').value = prefill;
  updateTextMeta();
  draftDirty = prefill.length > 0;
  $('#text-dialog').showModal();
  $('#text-body').focus();
}

function askConfirm(text) {
  return new Promise((resolve) => {
    const dialog = $('#confirm-dialog');
    $('#confirm-text').textContent = text;
    const finish = (value) => {
      dialog.close();
      $('#confirm-ok').onclick = null;
      $('#confirm-cancel').onclick = null;
      resolve(value);
    };
    $('#confirm-ok').onclick = () => finish(true);
    $('#confirm-cancel').onclick = () => finish(false);
    dialog.showModal();
  });
}

async function makeDir(name) {
  if (state.busy || !name) return;
  state.busy = true;
  try {
    const path = joinPath(state.cwd, name);
    await state.vault.commit((entries) => upsert(entries, { path, kind: 'dir', mtime: Date.now(), createdAt: new Date().toISOString() }));
    await refresh();
  } catch (e) {
    setMsg(statusEl, e.message, true);
  } finally {
    state.busy = false;
  }
}

async function authenticate(mode, { username, password, server }) {
  await pes.readySodium();
  const api = new Api(server);
  authMsg.classList.remove('error');
  setMsg(authMsg, '正在派生密钥（Argon2id，约需 0.1–1 秒）…');
  await tick();

  if (mode === 'register') {
    const salt = pes.sodium.randombytes_buf(16);
    const kdf = pes.DEFAULT_KDF;
    const masterKey = pes.deriveMasterKey(password, salt, kdf);
    const keys = pes.deriveKeys(masterKey);
    const res = await api.register({ username, authKey: pes.toHex(keys.authKey), salt: pes.toHex(salt), kdf });
    api.token = res.token;
    await enterMain({ api, userId: res.userId, keys, salt, kdf, masterKey, username, remember: $('#remember').checked });
    showRecovery(salt, masterKey);
    return;
  }

  let acct;
  try {
    acct = await api.account(username);
  } catch (e) {
    if (e.status === 404) throw new Error('账号不存在，请先注册');
    throw e;
  }
  const salt = pes.fromHex(acct.salt);
  const kdf = acct.kdf || pes.DEFAULT_KDF;
  const masterKey = pes.deriveMasterKey(password, salt, kdf);
  const keys = pes.deriveKeys(masterKey);
  const res = await api.login({ username, authKey: pes.toHex(keys.authKey) });
  api.token = res.token;
  await enterMain({ api, userId: res.userId, keys, salt, kdf, masterKey, username, remember: $('#remember').checked });
}

async function unlockWithRecovery(code, server, username) {
  await pes.readySodium();
  const { salt, masterKey } = decodeRecovery(code);
  const keys = pes.deriveKeys(masterKey);
  const api = new Api(server);
  const res = await api.login({ username, authKey: pes.toHex(keys.authKey) });
  api.token = res.token;
  const acct = await api.account(username);
  await enterMain({
    api,
    userId: res.userId,
    keys,
    salt,
    kdf: acct.kdf || pes.DEFAULT_KDF,
    masterKey,
    username,
    remember: $('#remember').checked,
  });
  setMsg($('#auth-msg'), '');
}

function setMode(mode) {
  state.mode = mode;
  for (const btn of document.querySelectorAll('.tabs button')) btn.classList.toggle('active', btn.dataset.mode === mode);
  $('#confirm-row').hidden = mode !== 'register';
  $('#auth-submit').textContent = mode === 'register' ? '注册并生成恢复码' : '登录';
}

document.querySelectorAll('.tabs button').forEach((btn) => {
  btn.onclick = () => setMode(btn.dataset.mode);
});

$('#auth-form').onsubmit = async (ev) => {
  ev.preventDefault();
  const username = $('#username').value.trim().toLowerCase();
  const password = $('#password').value;
  const server = $('#server').value.trim();
  if (!server) {
    $('#advanced').open = true;
    setMsg(authMsg, '请填写服务器地址', true);
    return;
  }
  if (state.mode === 'register' && password !== $('#password2').value) {
    setMsg(authMsg, '两次输入的密码不一致', true);
    return;
  }
  if (password.length < 8) {
    setMsg(authMsg, '密码至少 8 位（它是唯一的口令，请用长密码）', true);
    return;
  }
  $('#auth-submit').disabled = true;
  try {
    await authenticate(state.mode, { username, password, server });
  } catch (e) {
    setMsg(authMsg, e.message, true);
  } finally {
    $('#auth-submit').disabled = false;
  }
};

$('#recovery-form').onsubmit = async (ev) => {
  ev.preventDefault();
  const username = $('#username').value.trim().toLowerCase();
  const server = $('#server').value.trim();
  try {
    await unlockWithRecovery($('#recovery-code').value, server, username);
  } catch (e) {
    setMsg(authMsg, '恢复失败：' + e.message, true);
  }
};

$('#btn-upload').onclick = () => $('#file-input').click();
$('#file-input').onchange = (ev) => uploadFiles([...ev.target.files]);

$('#btn-text').onclick = () => openTextDialog('');
$('#text-body').oninput = () => {
  draftDirty = $('#text-body').value.length > 0;
  updateTextMeta();
  // Follow the first line until the user edits the name themselves.
  if ($('#text-name').value === autoName) {
    autoName = suggestTextName($('#text-body').value);
    $('#text-name').value = autoName;
  }
};
$('#text-cancel').onclick = async () => {
  if (draftDirty && $('#text-body').value.trim() && !(await askConfirm('放弃这段未保存的文本？'))) return;
  draftDirty = false;
  $('#text-dialog').close();
};
$('#text-form').onsubmit = (ev) => {
  ev.preventDefault();
  const body = $('#text-body').value;
  let name = $('#text-name').value.trim().replace(/\//g, '');
  if (!name || name === '.' || name === '..' || !body) {
    $('#text-hint').textContent = '名称和内容都不能为空';
    $('#text-hint').classList.add('error');
    return;
  }
  if (!/\.[A-Za-z0-9]{1,8}$/.test(name)) name += '.txt';
  $('#text-dialog').close();
  uploadText(name, body);
};
$('#preview-close').onclick = () => $('#preview-dialog').close();
$('#preview-copy').onclick = async () => {
  try {
    await navigator.clipboard.writeText(state.previewText);
    setMsg(statusEl, '已复制到剪贴板');
  } catch (e) {
    setMsg(statusEl, '复制失败：' + e.message, true);
  }
};

// Ctrl+V outside of an input opens the note dialog with the clipboard text.
window.addEventListener('paste', (ev) => {
  if (!state.vault || state.busy || $('#text-dialog').open) return;
  const el = document.activeElement;
  if (el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.isContentEditable)) return;
  const text = ev.clipboardData?.getData('text/plain') ?? '';
  if (!text.trim()) return;
  ev.preventDefault();
  openTextDialog(text);
});

window.addEventListener('beforeunload', (ev) => {
  if (!draftDirty) return;
  ev.preventDefault();
  ev.returnValue = '';
});

// Drag & drop anywhere on the page (only when unlocked).
let dragDepth = 0;
const dropOverlay = $('#drop-overlay');
const hasFiles = (ev) => [...(ev.dataTransfer?.types ?? [])].includes('Files');
window.addEventListener('dragenter', (ev) => {
  if (!state.vault || !hasFiles(ev)) return;
  ev.preventDefault();
  dragDepth++;
  dropOverlay.hidden = false;
});
window.addEventListener('dragover', (ev) => {
  if (!state.vault || !hasFiles(ev)) return;
  ev.preventDefault();
  ev.dataTransfer.dropEffect = 'copy';
});
window.addEventListener('dragleave', (ev) => {
  if (!state.vault) return;
  dragDepth = Math.max(0, dragDepth - 1);
  if (dragDepth === 0) dropOverlay.hidden = true;
});
window.addEventListener('drop', (ev) => {
  if (!state.vault) return;
  ev.preventDefault();
  dragDepth = 0;
  dropOverlay.hidden = true;
  const files = [...(ev.dataTransfer?.files ?? [])];
  if (files.length) uploadFiles(files);
});
$('#btn-mkdir').onclick = () => {
  $('#mkdir-name').value = '';
  $('#mkdir-dialog').showModal();
};
$('#mkdir-cancel').onclick = () => $('#mkdir-dialog').close();
$('#mkdir-form').onsubmit = (ev) => {
  ev.preventDefault();
  const name = $('#mkdir-name').value.trim();
  $('#mkdir-dialog').close();
  makeDir(name);
};
$('#btn-refresh').onclick = () => refresh().catch((e) => setMsg(statusEl, e.message, true));
$('#btn-recovery').onclick = () => showRecovery(state.salt, state.masterKey);
$('#btn-logout').onclick = async () => {
  await unlock.clearAll();
  location.reload();
};

// *.workers.dev is unreachable on some networks, so ignore a previously saved workers.dev URL.
const defaultServer = `${location.origin}/api`;
const storedServer = localStorage.getItem('pes.server') || '';
const activeServer =
  storedServer && !storedServer.includes('.workers.dev') ? storedServer : defaultServer;
$('#server').value = activeServer;

const serverHint = $('#server-hint');
if (activeServer !== defaultServer) {
  try {
    serverHint.textContent = `当前使用自定义服务器：${new URL(activeServer).host}`;
    serverHint.hidden = false;
  } catch {
    /* ignore unparsable stored value */
  }
}
$('#username').value = localStorage.getItem('pes.username') || '';
setMode('login');

// A remembered master key logs us straight back in: no password, no Argon2id (it only
// re-derives the subkeys and swaps the session token for a fresh one).
async function tryRestore() {
  let record;
  try {
    record = await unlock.restore();
  } catch {
    return false;
  }
  if (!record?.masterKey || !record?.username) return false;
  unlock.saveSession(record);
  setMsg(authMsg, `正在恢复 ${record.username} 的登录…`);
  try {
    await pes.readySodium();
    const api = new Api(record.server || defaultServer);
    const acct = await api.account(record.username);
    const salt = pes.fromHex(acct.salt);
    const kdf = acct.kdf || pes.DEFAULT_KDF;
    const masterKey = pes.fromHex(record.masterKey);
    const keys = pes.deriveKeys(masterKey);
    const res = await api.login({ username: record.username, authKey: pes.toHex(keys.authKey) });
    api.token = res.token;
    await enterMain({ api, userId: res.userId, keys, salt, kdf, masterKey, username: record.username });
    setMsg(authMsg, '');
    return true;
  } catch (e) {
    if (e.status === 401 || e.status === 404) {
      await unlock.clearAll();
      setMsg(authMsg, e.status === 404 ? '账号已不存在，请重新注册' : '记忆的密钥已失效，请重新登录', true);
    } else {
      setMsg(authMsg, `自动登录失败：${e.message}`, true);
    }
    return false;
  }
}

if (!window.isSecureContext) {
  setMsg(authMsg, '当前页面不是安全上下文（https 或 localhost），浏览器会禁用 WebCrypto，无法加密。', true);
} else {
  tryRestore();
}
